/**
 * App-timezone helpers (KC-363 follow-up).
 *
 * Cohort schedules are anchored to one fixed zone: admins pick wall-clock
 * open/close times in it, and every assignment is stored as UTC. These helpers
 * read a UTC `Date` back in that zone so the calendar and lists show the times
 * admins actually chose, independent of the viewer's own browser timezone
 * (which is what `date-fns format()` uses, hence the current display drift).
 *
 * Pass the zone `"local"` to read a Date in the browser's own zone — used by the
 * pre-creation Create-modal preview, whose illustrative windows are built in
 * local time rather than fetched from the API.
 */

export const APP_TIME_ZONE = "Europe/London";

const WEEKDAY_MON0: Record<string, number> = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
};

export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number;
  weekdayMon0: number; // 0 = Monday … 6 = Sunday
  minutesOfDay: number; // 0-1439
}

const partsFormatters = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(zone: string): Intl.DateTimeFormat {
  let fmt = partsFormatters.get(zone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: zone,
      weekday: "short",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    partsFormatters.set(zone, fmt);
  }
  return fmt;
}

/** Read a Date's calendar parts in `zone` ("local" = browser zone). */
export function zonedParts(date: Date, zone: string): ZonedParts {
  if (zone === "local") {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      weekdayMon0: (date.getDay() + 6) % 7,
      minutesOfDay: date.getHours() * 60 + date.getMinutes(),
    };
  }
  const parts: Record<string, string> = {};
  for (const p of partsFormatter(zone).formatToParts(date)) parts[p.type] = p.value;
  const hour = Number(parts.hour) % 24; // some engines emit "24" at midnight
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    weekdayMon0: WEEKDAY_MON0[parts.weekday] ?? 0,
    minutesOfDay: hour * 60 + Number(parts.minute),
  };
}

/** "HH:MM" (24-hour) in `zone`. */
export function formatZonedTime(date: Date, zone: string): string {
  const { minutesOfDay } = zonedParts(date, zone);
  const h = Math.floor(minutesOfDay / 60);
  const m = minutesOfDay % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

const labelFormatters = new Map<string, Intl.DateTimeFormat>();
function labelFormatter(key: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  let fmt = labelFormatters.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-GB", options);
    labelFormatters.set(key, fmt);
  }
  return fmt;
}

/** "Mon" in `zone`. */
export function formatZonedWeekday(date: Date, zone: string): string {
  return labelFormatter(`wd:${zone}`, {
    timeZone: zone === "local" ? undefined : zone,
    weekday: "short",
  }).format(date);
}

/** "20 Jul" in `zone`. */
export function formatZonedDayMonth(date: Date, zone: string): string {
  return labelFormatter(`dm:${zone}`, {
    timeZone: zone === "local" ? undefined : zone,
    day: "numeric",
    month: "short",
  }).format(date);
}

/** "YYYY-MM-DD" calendar date of `date` read in `zone` (for date inputs). */
export function zonedDateKey(date: Date, zone: string): string {
  const p = zonedParts(date, zone);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

/** Minutes the app zone is ahead of UTC at `instant` (positive = ahead). */
function appTzOffsetMinutes(instant: Date): number {
  const p = zonedParts(instant, APP_TIME_ZONE);
  const asIfUtc = Date.UTC(
    p.year,
    p.month - 1,
    p.day,
    Math.floor(p.minutesOfDay / 60),
    p.minutesOfDay % 60
  );
  return Math.round((asIfUtc - instant.getTime()) / 60000);
}

/**
 * Invert a wall-clock date + time in the app zone to the UTC instant it names.
 * e.g. ("2026-07-20", "12:00") in Europe/London (BST) -> 2026-07-20T11:00:00Z.
 * Used by the schedule editor to send exact instants back to the API without
 * shifting a cohort's chosen open/close time.
 */
export function appWallTimeToUtc(dateStr: string, timeStr: string): Date {
  const [y, mo, d] = dateStr.split("-").map(Number);
  const [h, mi] = timeStr.split(":").map(Number);
  const guess = Date.UTC(y, mo - 1, d, h || 0, mi || 0);
  const offset = appTzOffsetMinutes(new Date(guess));
  return new Date(guess - offset * 60000);
}
